import { useState, useEffect } from 'react';
import axios from 'axios';
import { Palette, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AmenagementPage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageRes = await axios.get(`${API}/pages/amenagement`);
        setPageSettings(pageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const services = [
    "Création de jardins complets",
    "Plantation de massifs et arbustes",
    "Installation de gazon",
    "Terrasses bois et pierre",
    "Allées et dallages",
    "Murets et clôtures",
    "Éclairage extérieur",
    "Fontaines et bassins"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-purple-50 to-white py-12 lg:py-20 pt-28 lg:pt-36" data-testid="amenagement-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Palette className="w-4 h-4 inline mr-2" />
              Aménagement paysager
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 mb-4">
              {pageSettings?.title || "Aménagement paysager"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Transformez votre extérieur avec nos services d'aménagement paysager sur mesure."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="amenagement-content">
        <div className="container-custom">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - First on mobile */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Demander un devis
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de devis sera configuré dans le back-office"
                />
              </div>
            </div>

            {/* Services - Second on mobile */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Nos prestations d'aménagement
              </h2>
              
              <div className="bg-purple-50 rounded-2xl p-6 mb-8">
                <p className="text-purple-800 font-semibold mb-2">Devis gratuit</p>
                <p className="text-purple-600 font-bold text-xl">Étude personnalisée</p>
                <p className="text-stone-600 text-sm mt-2">
                  Nous nous déplaçons pour évaluer votre projet
                </p>
              </div>

              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="text-stone-700">{service}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-purple-600" data-testid="amenagement-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            Un projet d'aménagement ?
          </h2>
          <p className="text-purple-100 mb-6">
            Contactez-nous pour un devis gratuit.
          </p>
          <a 
            href="tel:0465011022" 
            className="inline-flex items-center gap-2 bg-white text-purple-600 font-semibold px-8 py-4 rounded-full hover:bg-stone-100 transition-colors text-lg"
          >
            04 65 01 10 22
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default AmenagementPage;
